/** Minimal shape needed to sort production list rows. */
export type ProjectListItem = {
  id: string;
  title: string;
  updatedAt: string;
  createdAt?: string;
};

export type ProjectSortMode = "alpha" | "created" | "updated" | "custom";

const MODE_KEY = (userId: string) => `minproduktion.projectSort.${userId}`;
const ORDER_KEY = (userId: string) => `minproduktion.projectOrder.${userId}`;

function isSortMode(value: unknown): value is ProjectSortMode {
  return (
    value === "alpha" ||
    value === "created" ||
    value === "updated" ||
    value === "custom"
  );
}

export function loadProjectSortMode(userId: string): ProjectSortMode {
  try {
    const raw = localStorage.getItem(MODE_KEY(userId));
    if (isSortMode(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "updated";
}

export function saveProjectSortMode(
  userId: string,
  mode: ProjectSortMode,
): void {
  try {
    localStorage.setItem(MODE_KEY(userId), mode);
  } catch {
    /* ignore */
  }
}

export function loadProjectCustomOrder(userId: string): string[] {
  try {
    const raw = localStorage.getItem(ORDER_KEY(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

export function saveProjectCustomOrder(userId: string, ids: string[]): void {
  try {
    localStorage.setItem(ORDER_KEY(userId), JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function titleKey(p: ProjectListItem): string {
  return (p.title || "Unavngiven produktion").trim().toLocaleLowerCase("da");
}

function createdKey(p: ProjectListItem): string {
  return p.createdAt || p.updatedAt;
}

/** Sort a project list. Unknown custom-order ids are appended (newest first). */
export function sortProjectList<T extends ProjectListItem>(
  projects: T[],
  mode: ProjectSortMode,
  customOrder: string[] = [],
): T[] {
  if (projects.length <= 1) return projects.slice();

  if (mode === "alpha") {
    return projects
      .slice()
      .sort(
        (a, b) =>
          titleKey(a).localeCompare(titleKey(b), "da") ||
          b.updatedAt.localeCompare(a.updatedAt),
      );
  }

  if (mode === "created") {
    return projects
      .slice()
      .sort(
        (a, b) =>
          createdKey(b).localeCompare(createdKey(a)) ||
          titleKey(a).localeCompare(titleKey(b), "da"),
      );
  }

  if (mode === "updated") {
    return projects
      .slice()
      .sort(
        (a, b) =>
          b.updatedAt.localeCompare(a.updatedAt) ||
          titleKey(a).localeCompare(titleKey(b), "da"),
      );
  }

  // custom
  const byId = new Map(projects.map((p) => [p.id, p]));
  const seen = new Set<string>();
  const ordered: T[] = [];
  for (const id of customOrder) {
    const p = byId.get(id);
    if (!p || seen.has(id)) continue;
    seen.add(id);
    ordered.push(p);
  }
  const rest = projects
    .filter((p) => !seen.has(p.id))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return [...ordered, ...rest];
}

/** Merge current ids into a custom order (keep known order, append new). */
export function mergeProjectCustomOrder(
  existingOrder: string[],
  projectIds: string[],
): string[] {
  const want = new Set(projectIds);
  const next = existingOrder.filter((id) => want.has(id));
  const known = new Set(next);
  for (const id of projectIds) {
    if (!known.has(id)) next.push(id);
  }
  return next;
}
